import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { organizationRepository } from 'api/organizationRepository';
import InputField from 'components/fields/InputField';
import React, { useEffect, useState } from 'react';
import { flattenZodErrors, plantSchema } from 'validations/Validations';
import { z } from 'zod';

type EditPlantModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  branch: any;
  onBranchUpdated: (updatedBranches: any[]) => void;
  showCenteredAlert: (
    msg: string,
    status?: 'success' | 'info' | 'warning' | 'error'
  ) => void;
};

const EditPlantModal: React.FC<EditPlantModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  branch,
  onBranchUpdated,
  showCenteredAlert,
}) => {
  const [editBranch, setEditBranch] = useState<any>(branch);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const cardBg = useColorModeValue('white', 'navy.800');

  useEffect(() => setEditBranch(branch), [branch]);

  const handleChange = (field: string, value: string | boolean, parent?: string) => {
    if (parent) {
      setEditBranch((prev: any) => ({
        ...prev,
        [parent]: { ...prev[parent], [field]: value },
      }));

      const parentShape = (plantSchema.shape as any)[parent] as
        | z.ZodObject<any>
        | undefined;
      const schemaField = parentShape?.shape?.[field];
      if (!schemaField) return;

      const result = schemaField.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? '' : result.error.issues[0].message,
      }));
    } else {
      setEditBranch((prev: any) => ({ ...prev, [field]: value }));

      const schemaField = (plantSchema.shape as Record<string, z.ZodTypeAny>)[field];
      if (!schemaField) return;

      const result = schemaField.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };

  const handleUpdate = async () => {
    try {
      plantSchema.parse({
        name: editBranch.name,
        address: {
          street: editBranch.address?.street || '',
          city: editBranch.address?.city || '',
          state: editBranch.address?.state || '',
          zipCode: editBranch.address?.zipCode || '',
          country: editBranch.address?.country || '',
        },
        isActive: editBranch.isActive,
      });

      setErrors({});
      setIsUpdating(true);

      const payload = {
        name: editBranch.name,
        isActive: editBranch.isActive,
        address: {
          ...editBranch.address,
          name: editBranch.name,
        },
      };

      await organizationRepository.updateBranch(
        organizationId,
        editBranch.locationId,
        payload
      );

      const updatedOrg = await organizationRepository.getOrganizationById(
        organizationId
      );
      onBranchUpdated(updatedOrg?.locations || []);

      showCenteredAlert('Plant updated successfully', 'success');
      onClose();
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors(flattenZodErrors(err));
        showCenteredAlert('Please fix the validation errors.', 'warning');
      } else {
        showCenteredAlert('Failed to update plant', 'error');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (!editBranch) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={cardBg}>
        <ModalHeader>Edit Plant</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Plant Name
                </Text>
                <InputField
                  value={editBranch.name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)}
                />
                {errors.name && (
                  <Text fontSize="xs" color="red.500">
                    {errors.name}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Street
                </Text>
                <InputField
                  value={editBranch.address?.street || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('street', e.target.value, 'address')}
                />
                {errors.street && (
                  <Text fontSize="xs" color="red.500">
                    {errors.street}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  City
                </Text>
                <InputField
                  value={editBranch.address?.city || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('city', e.target.value, 'address')}
                />
                {errors.city && (
                  <Text fontSize="xs" color="red.500">
                    {errors.city}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Status
                </Text>
                <Select
                  value={
                    editBranch.isActive === true
                      ? 'true'
                      : editBranch.isActive === false
                        ? 'false'
                        : ''
                  }
                  onChange={(e) => handleChange('isActive', e.target.value === 'true')}
                >
                  <option value="">Select Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
                {errors.isActive && (
                  <Text fontSize="xs" color="red.500">
                    {errors.isActive}
                  </Text>
                )}
              </Box>
            </VStack>

            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  State
                </Text>
                <InputField
                  value={editBranch.address?.state || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('state', e.target.value, 'address')}
                />
                {errors.state && (
                  <Text fontSize="xs" color="red.500">
                    {errors.state}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  ZIP Code
                </Text>
                <InputField
                  value={editBranch.address?.zipCode || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('zipCode', e.target.value, 'address')}
                />
                {errors.zipCode && (
                  <Text fontSize="xs" color="red.500">
                    {errors.zipCode}
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Country
                </Text>
                <InputField
                  value={editBranch.address?.country || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('country', e.target.value, 'address')}
                />
                {errors.country && (
                  <Text fontSize="xs" color="red.500">
                    {errors.country}
                  </Text>
                )}
              </Box>
            </VStack>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter justifyContent="center">
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleUpdate}
            isLoading={isUpdating}
          >
            Update
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditPlantModal;
